import { gql } from '@apollo/client/core'

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  const modelFormLock = {
    observationResponse: null
  }

  modelFormLock.unsubscribe = () => {
    if (!_.isNil(modelFormLock.observationResponse)) {
      modelFormLock.observationResponse.unsubscribe()
    }
  }

  modelFormLock.subscribe = () => {
    if (!app.config.globalProperties.$cable) return

    modelFormLock.unsubscribe()

    const subQuery = gql`subscription {
      modelFormLockSent {
        modelFormLock {
          formName
          allUsers
          userId
          modelUpdated
          modelUpdatedUserId
          modelUpdatedUserString
        }
      }
    }`

    const observer = app.apolloProvider.defaultClient.subscribe({
      query: subQuery
    })

    modelFormLock.observationResponse = observer.subscribe({
      next (data) {
        if (modelFormLock?.handleLockNotification) {
          modelFormLock.handleLockNotification(data)
        }
      },
      error (error) {
        console.error(error)
      }
    })
  }

  modelFormLock.leaveForm = async (formName) => {
    const command = 'leave_form'
    try {
      const mutation = gql`mutation ($input: SendModelFormLockInput!){
      sendModelFormLock(input: $input) {
        formName
        allUsers
        userId
        errors {
          code
          description
        }
      }
    }`

      await app.apolloProvider.defaultClient.mutate({
        mutation,
        variables: {
          input: {
            modelFormLockInput: {
              command,
              formName
            }
          }
        }
      })
    } catch (exception) {
      if (app.config.globalProperties.$rollbar) {
        app.config.globalProperties.$rollbar.error(command + ' failed', exception)
      }
    }
  }

  modelFormLock.savedChangeToModel = async (formName) => {
    const command = 'saved_change_to_model'
    try {
      const mutation = gql`mutation ($input: SendModelFormLockInput!){
      sendModelFormLock(input: $input) {
        formName
        allUsers
        userId
        errors {
          code
          description
        }
      }
    }`

      await app.apolloProvider.defaultClient.mutate({
        mutation,
        variables: {
          input: {
            modelFormLockInput: {
              command,
              formName
            }
          }
        }
      })
    } catch (exception) {
      if (app.config.globalProperties.$rollbar) {
        app.config.globalProperties.$rollbar.error(command + ' failed', exception)
      }
    }
  }

  modelFormLock.viewForm = async (formName, cableNotification = null) => {
    const command = 'view_form'
    try {
      const mutation = gql`mutation ($input: SendModelFormLockInput!){
      sendModelFormLock(input: $input) {
        formName
        allUsers
        userId
        errors {
          code
          description
        }
      }
    }`

      const result = cableNotification || await app.apolloProvider.defaultClient.mutate({
        mutation,
        variables: {
          input: {
            modelFormLockInput: {
              command,
              formName
            }
          }
        }
      })
      const lockedUser = result.data.sendModelFormLock.userId
      const modelLocked = !!lockedUser
      const lockedUserId = lockedUser
      const allViewingUsers = _.map(result.data.sendModelFormLock.allUsers, user => {
        const values = user.split(',')
        return {
          id: values[0],
          firstName: values[1],
          lastName: values[2],
          locked: values[0] === lockedUser?.toString()
        }
      })

      return {
        lockedUserId,
        modelLocked,
        allViewingUsers,
        modelUpdated: result.data.sendModelFormLock.modelUpdated,
        modelUpdatedUserId: result.data.sendModelFormLock.modelUpdatedUserId,
        modelUpdatedUserString: result.data.sendModelFormLock.modelUpdatedUserString
      }
    } catch (exception) {
      if (app.config.globalProperties.$rollbar) {
        app.config.globalProperties.$rollbar.error(command + ' failed', exception)
        return {
          errors: ['Failed to retrieve lock status']
        }
      }
    }
  }

  modelFormLock.lockForm = async (formName) => {
    const command = 'get_lock'

    try {
      const mutation = gql`mutation ($input: SendModelFormLockInput!){
      sendModelFormLock(input: $input) {
        formName
        allUsers
        userId
        errors {
          code
          description
        }
      }
    }`

      const result = await app.apolloProvider.defaultClient.mutate({
        mutation,
        variables: {
          input: {
            modelFormLockInput: {
              command,
              formName
            }
          }
        }
      })
      const lockedUser = result.data.sendModelFormLock.userId
      const modelLocked = !!lockedUser

      const allViewingUsers = _.map(result.data.sendModelFormLock.allUsers, user => {
        const values = user.split(',')
        return {
          id: values[0],
          firstName: values[1],
          lastName: values[2],
          locked: values[0] === lockedUser?.toString()
        }
      })

      return {
        lockedUserId: lockedUser,
        modelLocked,
        allViewingUsers
      }
    } catch (exception) {
      if (app.config.globalProperties.$rollbar) {
        app.config.globalProperties.$rollbar.error(command + ' failed', exception)
        return {
          errors: ['Failed to retrieve lock status']
        }
      }
    }
  }

  modelFormLock.releaseLock = async (formName) => {
    const command = 'release_lock'

    try {
      const mutation = gql`mutation ($input: SendModelFormLockInput!){
      sendModelFormLock(input: $input) {
        formName
        allUsers
        userId
        errors {
          code
          description
        }
      }
    }`

      const result = await app.apolloProvider.defaultClient.mutate({
        mutation,
        variables: {
          input: {
            modelFormLockInput: {
              command,
              formName
            }
          }
        }
      })
      const lockedUser = result.data.sendModelFormLock.userId
      const modelLocked = !!lockedUser

      const allViewingUsers = _.map(result.data.sendModelFormLock.allUsers, user => {
        const values = user.split(',')
        return {
          id: values[0],
          firstName: values[1],
          lastName: values[2],
          locked: values[0] === lockedUser?.toString()
        }
      })

      return {
        lockedUserId: lockedUser,
        modelLocked,
        allViewingUsers
      }
    } catch (exception) {
      if (app.config.globalProperties.$rollbar) {
        app.config.globalProperties.$rollbar.error(command + ' failed', exception)
        return {
          errors: ['Failed to retrieve lock status']
        }
      }
    }
  }

  app.config.globalProperties.$modelFormLock = modelFormLock
  app.$modelFormLock = modelFormLock
  window.$modelFormLock = modelFormLock
}
